import { useState } from "react";
import { 
  Upload, 
  Palette, 
  Sparkles, 
  ShoppingCart,
  BookOpen,
  Crown,
  ChefHat,
  Briefcase,
  Flower2,
  Toilet,
  Check,
  ChevronRight,
  Star,
  ArrowRight,
  ChevronDown,
  Menu,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SCENARIOS = [
  { 
    id: "bookworm", 
    name: "Bookworm", 
    description: "Reading a book in a cozy armchair",
    icon: BookOpen,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/abmistfi_bookworm%20Realistic%20style.png"
  },
  { 
    id: "throne_time", 
    name: "Throne Time", 
    description: "On the porcelain throne",
    icon: Toilet,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/jsuf1uw0_throne%20time%20realistic.png"
  },
  { 
    id: "master_chef", 
    name: "Master Chef", 
    description: "Cooking up a storm",
    icon: ChefHat,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/3dofs2ai_master%20chef%20realistic.png"
  },
  { 
    id: "the_boss", 
    name: "The Boss", 
    description: "Running the company",
    icon: Briefcase,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/zkwuhdf9_boss%20watercolour.png"
  },
  { 
    id: "green_paws", 
    name: "Green Paws", 
    description: "Tending the garden",
    icon: Flower2,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/tnxfmqh1_green%20paws%20watercolour.png"
  },
  { 
    id: "royalty", 
    name: "Royalty", 
    description: "Reigning supreme",
    icon: Crown,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/5czcbi20_royalty%20vintage%20style.png"
  },
];

const STEPS = [
  { icon: Upload, title: "Upload", description: "Upload your pet's photo" },
  { icon: Palette, title: "Choose", description: "Pick a fun scenario" },
  { icon: Sparkles, title: "Generate", description: "AI creates your portrait" },
  { icon: ShoppingCart, title: "Order", description: "Get your masterpiece" },
];

const PRICING = [
  { 
    id: "digital", 
    name: "Digital Download", 
    price: "£9.99", 
    features: ["All 4 portraits included", "High-resolution PNG", "Instant download", "Print-ready files"],
    popular: false
  },
  { 
    id: "print_12x16", 
    name: "Premium Print 12\"×16\"", 
    price: "£24.99", 
    features: ["All 4 digital portraits FREE", "Premium matte paper", "Museum-quality print", "Free UK shipping"],
    popular: false
  },
  { 
    id: "print_18x24", 
    name: "Premium Print 18\"×24\"", 
    price: "£29.99", 
    features: ["All 4 digital portraits FREE", "Premium matte paper", "Museum-quality print", "Free UK shipping"],
    popular: true
  },
  { 
    id: "print_24x36", 
    name: "Premium Print 24\"×36\"", 
    price: "£34.99", 
    features: ["All 4 digital portraits FREE", "Premium matte paper", "Museum-quality print", "Free UK shipping"],
    popular: false
  },
];

const FAQS = [
  {
    question: "How long does it take to generate my portrait?",
    answer: "Our AI typically generates your unique pet portrait in 1-2 minutes. You'll see 4 different variations to choose from!"
  },
  {
    question: "What kind of photos work best?",
    answer: "Clear, well-lit photos with your pet facing the camera work best. The AI needs to see your pet's face clearly to create the best portrait."
  },
  {
    question: "Can I use photos of any pet?",
    answer: "Yes! Pawtraits works with dogs, cats, rabbits, hamsters, and most other pets. If it has a cute face, we can portrait it!"
  },
  {
    question: "What if I'm not happy with the results?",
    answer: "We offer a satisfaction guarantee. If you're not happy with your portrait, contact us and we'll generate a new one or refund your purchase."
  },
  {
    question: "How do prints get delivered?",
    answer: "We ship all prints within 2-3 business days. UK delivery is free and typically takes 3-5 days. International shipping is available."
  },
];

export const LandingPage = ({ onGetStarted }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🐾</span>
              <span className="font-bold text-xl text-[#2d2a26]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Pawtraits
              </span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#scenarios" className="text-[#2d2a26]/70 hover:text-[#7c5cff] transition-colors font-medium">
                Scenarios
              </a>
              <a href="#how-it-works" className="text-[#2d2a26]/70 hover:text-[#7c5cff] transition-colors font-medium">
                How It Works
              </a>
              <a href="#pricing" className="text-[#2d2a26]/70 hover:text-[#7c5cff] transition-colors font-medium">
                Pricing
              </a>
              <a href="#faq" className="text-[#2d2a26]/70 hover:text-[#7c5cff] transition-colors font-medium">
                FAQ
              </a>
            </div>

            <div className="hidden md:block">
              <Button 
                onClick={onGetStarted}
                data-testid="nav-get-started-btn"
                className="bg-[#7c5cff] hover:bg-[#6a4de6] text-white rounded-full px-6 shadow-[0_8px_30px_rgb(124,92,255,0.3)] hover:shadow-[0_8px_30px_rgb(124,92,255,0.5)] hover:scale-105 transition-all duration-300"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-white/20">
            <div className="px-4 py-4 space-y-3">
              <a href="#scenarios" className="block py-2 text-[#2d2a26]/70 font-medium">Scenarios</a>
              <a href="#how-it-works" className="block py-2 text-[#2d2a26]/70 font-medium">How It Works</a>
              <a href="#pricing" className="block py-2 text-[#2d2a26]/70 font-medium">Pricing</a>
              <a href="#faq" className="block py-2 text-[#2d2a26]/70 font-medium">FAQ</a>
              <Button 
                onClick={onGetStarted}
                data-testid="mobile-get-started-btn"
                className="w-full bg-[#7c5cff] hover:bg-[#6a4de6] text-white rounded-full"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="space-y-8 animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-[#e0d8ff] text-[#7c5cff] px-4 py-2 rounded-full text-sm font-medium">
                <Sparkles size={16} />
                AI-Powered Pet Portraits
              </div>
              
              <h1 
                className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#2d2a26] leading-tight"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Turn Your Pet Into a{" "}
                <span className="text-[#7c5cff]">Masterpiece</span>
              </h1>
              
              <p className="text-xl text-[#2d2a26]/70 max-w-lg">
                Upload a photo of YOUR furry friend and our AI will transform THEM into hilarious, 
                beautiful portraits - <strong>personalised with their name!</strong> From royal kings to master chefs!
              </p>

              {/* Key Benefits */}
              <div className="flex flex-wrap gap-3">
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                  ✨ All 4 portraits included
                </span>
                <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">
                  🏷️ Personalised with pet's name
                </span>
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                  🚚 Free UK shipping
                </span>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={onGetStarted}
                  data-testid="hero-get-started-btn"
                  size="lg"
                  className="bg-[#7c5cff] hover:bg-[#6a4de6] text-white rounded-full px-8 py-6 text-lg shadow-[0_8px_30px_rgb(124,92,255,0.3)] hover:shadow-[0_8px_30px_rgb(124,92,255,0.5)] hover:scale-105 transition-all duration-300"
                >
                  Create Your Portrait
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  data-testid="view-examples-btn"
                  onClick={() => document.getElementById('scenarios').scrollIntoView({ behavior: 'smooth' })}
                  className="rounded-full px-8 py-6 text-lg border-2 border-[#2d2a26]/20 hover:border-[#7c5cff] hover:text-[#7c5cff] transition-all duration-300"
                >
                  View Examples
                </Button>
              </div>

              {/* Social Proof */}
              <div className="flex items-center gap-4 pt-4">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ffb347] to-[#7c5cff] border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-[#ffb347]">
                    {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="currentColor" />)}
                  </div>
                  <p className="text-sm text-[#2d2a26]/60">10,000+ happy pet parents</p>
                </div>
              </div>
            </div>

            {/* Right - Hero Before/After Showcase */}
            <div className="relative animate-slide-up stagger-2">
              <div className="relative z-10">
                {/* Before/After Transformation */}
                <div className="relative bg-white rounded-3xl shadow-2xl p-6 max-w-lg mx-auto">
                  <div className="grid grid-cols-2 gap-4 items-center">
                    {/* Before - Original Pet Photo */}
                    <div className="relative">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/9311onz6_From%20this%20to%20this.JPG"
                        alt="Original pet photo"
                        className="rounded-2xl w-full aspect-square object-cover shadow-md"
                      />
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white px-3 py-1 rounded-full text-xs font-semibold text-[#2d2a26] shadow-md whitespace-nowrap">
                        Your Photo
                      </div>
                    </div>
                    
                    {/* After - AI Generated Portrait */}
                    <div className="relative">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/20fwu7zx_To%20this.png"
                        alt="AI generated portrait"
                        className="rounded-2xl w-full object-cover shadow-md"
                      />
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#7c5cff] px-3 py-1 rounded-full text-xs font-semibold text-white shadow-md whitespace-nowrap">
                        AI Masterpiece
                      </div>
                    </div>
                  </div>
                  
                  {/* Arrow between images */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                    <div className="w-12 h-12 bg-[#7c5cff] rounded-full flex items-center justify-center shadow-lg">
                      <ArrowRight size={24} className="text-white" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#ffb347] rounded-2xl rotate-12 animate-float opacity-80" />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#e0d8ff] rounded-full animate-float" style={{ animationDelay: '1s' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios Section */}
      <section id="scenarios" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#2d2a26] mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Choose Your Scenario
            </h2>
            <p className="text-xl text-[#2d2a26]/70 max-w-2xl mx-auto">
              Pick a fun scenario and watch your pet transform into something magical
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SCENARIOS.map((scenario, index) => {
              const Icon = scenario.icon;
              return (
                <div 
                  key={scenario.id}
                  data-testid={`scenario-card-${scenario.id}`}
                  className={`scenario-card bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] cursor-pointer animate-slide-up ${
                    index === 0 || index === 5 ? 'md:col-span-2 lg:col-span-1' : ''
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={onGetStarted}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={scenario.image}
                      alt={scenario.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#e0d8ff] rounded-xl flex items-center justify-center text-[#7c5cff]">
                        <Icon size={20} />
                      </div>
                      <h3 
                        className="text-xl font-bold text-[#2d2a26]"
                        style={{ fontFamily: 'Fredoka, sans-serif' }}
                      >
                        {scenario.name}
                      </h3>
                    </div>
                    <p className="text-[#2d2a26]/60">{scenario.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#2d2a26] mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              How It Works
            </h2>
            <p className="text-xl text-[#2d2a26]/70 max-w-2xl mx-auto">
              Creating your pet's portrait is as easy as 1-2-3-4!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <div 
                  key={step.title}
                  className="text-center animate-slide-up"
                  style={{ animationDelay: `${index * 0.15}s` }}
                  data-testid={`step-${index + 1}`}
                >
                  <div className="relative mb-6">
                    <div className="w-20 h-20 mx-auto bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] flex items-center justify-center text-[#7c5cff]">
                      <Icon size={32} />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#7c5cff] rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                    {index < STEPS.length - 1 && (
                      <div className="hidden lg:block absolute top-1/2 left-full w-full h-0.5 bg-gradient-to-r from-[#7c5cff]/30 to-transparent -translate-y-1/2" />
                    )}
                  </div>
                  <h3 
                    className="text-xl font-bold text-[#2d2a26] mb-2"
                    style={{ fontFamily: 'Fredoka, sans-serif' }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-[#2d2a26]/60">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#2d2a26] mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Simple Pricing
            </h2>
            <p className="text-xl text-[#2d2a26]/70 max-w-2xl mx-auto">
              Choose how you'd like to receive your masterpiece
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PRICING.map((plan, index) => (
              <div 
                key={plan.id}
                data-testid={`pricing-card-${plan.id}`}
                className={`relative bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-slide-up ${
                  plan.popular ? 'ring-2 ring-[#7c5cff] scale-105' : ''
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#7c5cff] text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <h3 
                  className="text-2xl font-bold text-[#2d2a26] mb-2"
                  style={{ fontFamily: 'Fredoka, sans-serif' }}
                >
                  {plan.name}
                </h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#7c5cff]">{plan.price}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#2d2a26]/70">
                      <Check size={18} className="text-[#10b981]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={onGetStarted}
                  className={`w-full rounded-full py-6 text-lg ${
                    plan.popular 
                      ? 'bg-[#7c5cff] hover:bg-[#6a4de6] text-white shadow-[0_8px_30px_rgb(124,92,255,0.3)]' 
                      : 'bg-[#f8f5f2] hover:bg-[#e0d8ff] text-[#2d2a26]'
                  } transition-all duration-300`}
                >
                  Choose {plan.name.split(' ')[0]}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#2d2a26] mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Questions? We've Got Answers
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                data-testid={`faq-item-${index}`}
                className="bg-white rounded-2xl px-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-none"
              >
                <AccordionTrigger className="text-left font-semibold text-[#2d2a26] hover:no-underline py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#2d2a26]/70 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[#7c5cff] to-[#6a4de6] rounded-3xl p-12 md:p-16 text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-10 left-10 text-6xl">🐾</div>
              <div className="absolute bottom-10 right-10 text-6xl">🐾</div>
              <div className="absolute top-1/2 left-1/4 text-4xl">🐾</div>
            </div>
            <div className="relative z-10">
              <h2 
                className="text-4xl md:text-5xl font-bold mb-4"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Ready to Create Magic?
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Join thousands of pet parents who have immortalized their furry friends in art. 
                It only takes 2 minutes!
              </p>
              <Button 
                onClick={onGetStarted}
                data-testid="cta-get-started-btn"
                size="lg"
                className="bg-white text-[#7c5cff] hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold hover:scale-105 transition-all duration-300"
              >
                Create My Portrait Now
                <ChevronRight className="ml-2" size={20} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-[#e6e0dc]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🐾</span>
              <span className="font-bold text-xl text-[#2d2a26]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Pawtraits
              </span>
            </div>
            <p className="text-[#2d2a26]/60 text-sm">
              © 2025 Pawtraits. Made with ❤️ for pet lovers everywhere.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
